import React from "react";
import { Box, Text, useColorModeValue } from "@chakra-ui/react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Plugin as ChartPlugin,
  ChartOptions,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";

ChartJS.register(ArcElement, Tooltip, Legend);

// Value-only labels with white halo + leader lines
const valueOnlyLabelsDoughnut: ChartPlugin<"doughnut"> = {
  id: "valueOnlyLabelsDoughnut",
  afterDraw(chart) {
    const { ctx } = chart;
    const ds = chart.data.datasets?.[0];
    if (!ds || (ds.data as number[]).every((v) => Number(v) === 0)) return;

    const meta = chart.getDatasetMeta(0);
    meta.data.forEach((el: any, i: number) => {
      const value = Number(ds.data[i] ?? 0);
      if (!value) return;

      const mid = (el.startAngle + el.endAngle) / 2;
      const r = el.outerRadius;
      const cx = el.x, cy = el.y;

      const x1 = cx + Math.cos(mid) * r;
      const y1 = cy + Math.sin(mid) * r;
      const x2 = cx + Math.cos(mid) * (r + 16);
      const y2 = cy + Math.sin(mid) * (r + 16);

      ctx.save();
      // leader line
      ctx.strokeStyle = "#94a3b8"; // slate-400
      ctx.lineWidth = 1.25;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();

      // value text with halo
      const leftSide = !(mid > Math.PI / 2 && mid < (3 * Math.PI) / 2);
      ctx.font =
        "600 12px Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif";
      ctx.textBaseline = "middle";
      ctx.textAlign = leftSide ? "left" : "right";
      ctx.lineWidth = 3;
      ctx.strokeStyle = "#ffffff";
      ctx.strokeText(String(value), x2 + (leftSide ? 5 : -5), y2);
      ctx.fillStyle = "#111827"; // gray-900
      ctx.fillText(String(value), x2 + (leftSide ? 5 : -5), y2);
      ctx.restore();
    });
  },
};
ChartJS.register(valueOnlyLabelsDoughnut);

interface TaskCompletionChartProps {
  totalTasks: number;
  completedTasks: number;
  remainingTasks: number;
}

const TaskCompletionChart: React.FC<TaskCompletionChartProps> = ({
  totalTasks,
  completedTasks,
  remainingTasks,
}) => {
  const text = useColorModeValue("#111827", "#E5E7EB");
  const textMuted = useColorModeValue("#6B7280", "#C3C9D3");
  const ring = useColorModeValue("#CBD5E0", "#374151");
  const completedFill = useColorModeValue("#22C55E", "#16A34A");
  const remainingFill = useColorModeValue("#E2E8F0", "#374151");
  const completionPercentage =
    totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const chartData = {
    labels: ["Completed", "Remaining"],
    datasets: [
      {
        data: [completedTasks, remainingTasks],
        backgroundColor: [
          completionPercentage === 0 ? ring : completedFill,
          remainingFill,
        ],
        borderColor: [
          completionPercentage === 0 ? ring : completedFill,
          ring,
        ],
        borderWidth: 1,
      },
    ],
  };

  const options: ChartOptions<"doughnut"> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "70%",
    // Balanced padding so labels never clip; keeps chart visually centered
    layout: { padding: { top: 22, right: 22, bottom: 22, left: 22 } },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
  };

  return (
    // Center chart like the left card; legend sits naturally below
    <Box className="flex flex-col items-center justify-center w-full h-full">
      {/* Increased size to 200×200 to mirror Task Overview */}
      <Box position="relative" width="200px" height="200px" mb={4}>
        <Doughnut data={chartData} options={options} />
        <Box
          position="absolute"
          inset={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          pointerEvents="none"
        >
          <Text
            fontSize="md"
            fontWeight="bold"
            color={completionPercentage === 0 ? textMuted : completedFill}
            lineHeight="1"
            whiteSpace="nowrap"
          >
            {completionPercentage}%
          </Text>
        </Box>
      </Box>

      {/* Legend: same row height/baseline as Task Overview legend */}
      <Box className="flex flex-wrap justify-center gap-3 px-2" style={{ minHeight: 20 }}>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ring }} />
          <span className="text-xs" style={{ color: textMuted }}>
            Total: <span className="font-medium" style={{ color: text }}>{totalTasks}</span>
          </span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: completedFill }} />
          <span className="text-xs" style={{ color: textMuted }}>
            Completed: <span className="font-medium" style={{ color: text }}>{completedTasks}</span>
          </span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: remainingFill }} />
          <span className="text-xs" style={{ color: textMuted }}>
            Remaining: <span className="font-medium" style={{ color: text }}>{remainingTasks}</span>
          </span>
        </div>
      </Box>
    </Box>
  );
};

export default TaskCompletionChart;